import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Ban, CalendarClock, CircleDollarSign, Download, Loader2, Pencil, Percent, Plus, Receipt, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { getApiErrorMessage, getBlobApiErrorMessage } from "../../api/client";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { regionName } from "../../components/ProvinceSelect";
import { useAuth } from "../../contexts/AuthContext";
import {
  fetchTruckingInvoicePdf, useSaveTruckingInvoiceLines, useSendTruckingInvoice, useTruckingInvoice, useTruckingInvoiceRecipient,
  useVoidTruckingInvoice,
} from "../../data/truckingData";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { saveBlobAs } from "../../lib/blob-tab";
import { formatCalendarDate, formatInstant, formatInstantTenantDay } from "../../lib/calendar-date";
import { fmtCurrency } from "../../lib/display-labels";
import { truckingInvoiceStatusSkin as statusSkin } from "../../lib/trucking-invoice-status";
import { queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { CcField, RecipientPreview, parseCcInput } from "../downhole/sendBits";
import type { TruckingInvoice, TruckingInvoiceLine, TruckingInvoiceLineWrite } from "../../types/trucking-invoices";

/**
 * /trucking/invoices/:id — one trucking invoice (api#954, 0477). Opened from
 * Finance → Invoices, the one list across verticals.
 *
 * STORED FIGURES ONLY. Line amounts, subtotal, each tax line and the total are
 * the invoice's own; nothing is recomputed here, and an absent figure is `—`,
 * never 0. Every amount is in the INVOICE's currency, and the total says which.
 *
 * TAX is the stored snapshot for the work's jurisdiction: one row per recorded
 * tax line (`tax_rate_1_label` / `_percent` / `_amount`, then `_2_*`). An
 * invoice from before 0477 has only the blended `tax_rate` and `tax_amount`,
 * and shows them as one "Tax (x%)" row, never under a guessed label.
 *
 * WRITES (admin, office staff; the API's INVOICE_WRITE):
 *   Edit lines   a draft only. PATCH /:id/line-items with every line's rate and
 *                quantity as typed; the API refuses an incomplete line (422
 *                LINE_ITEM_INCOMPLETE, `problems`), shown as it words them, and
 *                answers with the new totals.
 *   Send         To is pre-filled with who the API would send to (GET
 *                /:id/recipient, api#1006, the send's own resolver) and stays
 *                editable, with up to ten CCs. A To left as pre-filled is not
 *                sent, so the API records where the address came from. Every
 *                refusal (400 / 409 / 502) is shown as worded.
 *   Void         draft, sent or approved only (api#1016), with a required
 *                reason. Never offered on a paid or void invoice. Refusals
 *                (REASON_REQUIRED, INVOICE_PAID, INVOICE_VOID) shown as worded.
 * Download PDF: the API's PDF, also for supervisors and dispatchers.
 *
 * VOID IS FINAL. A void invoice offers no Send, no line edit and no Void; it
 * shows the reason, who voided it (`voided_by_name`, api#1021; `—` when the
 * API names nobody) and when. Its BOLs are released and read "No invoice"
 * again, from their own `billed_on` (fe#971).
 */

const READ_ROLES = ["admin", "office_staff", "supervisor", "dispatcher"];
const WRITE_ROLES = ["admin", "office_staff"];
/** The statuses the API voids (api#1016). A list, not "not paid": an unknown status offers nothing. */
const VOIDABLE = ["draft", "sent", "approved"];
/** The statuses the API sends; paid and void are not among them. */
const SENDABLE = ["draft", "sent", "approved"];

const LINE_TYPE: Record<string, string> = {
  load_rate: "Haul",
  fuel_surcharge: "Fuel surcharge",
  wait_time: "Wait time",
  carrier_passthrough: "Carrier passthrough",
  damage: "Damage",
  other: "Other",
};
const lineTypeLabel = (t: unknown) => (typeof t === "string" && LINE_TYPE[t]) || "—";

const SENT_SOURCE: Record<string, string> = {
  entered: "typed on the send",
  accounting_email: "accounting email",
  main_contact_email: "main contact",
};

const num = (v: unknown) => (v === null || v === undefined || v === "" ? null : String(v));
const pct = (v: unknown) => (num(v) === null ? null : `${Number(v)}%`);

/** One row per recorded tax line; a pre-0477 invoice's blended rate as one "Tax (x%)" row. */
export function taxRows(inv: TruckingInvoice): { label: string; amount: string | null }[] {
  const rows: { label: string; amount: string | null }[] = [];
  if (inv.tax_rate_1_label) {
    rows.push({ label: `${inv.tax_rate_1_label}${pct(inv.tax_rate_1_percent) ? ` (${pct(inv.tax_rate_1_percent)})` : ""}`, amount: num(inv.tax_rate_1_amount) });
    if (inv.tax_rate_2_label) {
      rows.push({ label: `${inv.tax_rate_2_label}${pct(inv.tax_rate_2_percent) ? ` (${pct(inv.tax_rate_2_percent)})` : ""}`, amount: num(inv.tax_rate_2_amount) });
    }
    return rows;
  }
  rows.push({ label: `Tax${pct(inv.tax_rate) ? ` (${pct(inv.tax_rate)})` : ""}`, amount: num(inv.tax_amount) });
  return rows;
}

// Filled with the band's own navy: unfilled, the hero circle's ring showed
// through behind the label and took it to 4.58:1 at 1856-2080 wide (dark).
const BAND_SECONDARY =
  "inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 bg-fi-brand px-4 text-sm font-bold transition hover:bg-white/10 disabled:opacity-50";
const SECONDARY =
  "inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm font-medium text-fieldiq-text hover:bg-fieldiq-dark/50 disabled:opacity-50";
const INPUT =
  "h-9 w-full min-w-0 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-2 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none";

export default function TruckingInvoiceDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const { user } = useAuth();
  const role = (user as { role?: string } | null)?.role ?? "";
  const canRead = READ_ROLES.includes(role);
  const canWrite = WRITE_ROLES.includes(role);
  const query = useTruckingInvoice(canRead ? id : "");
  const failure = queryLoadFailure(query);
  const inv = failure ? undefined : query.data;
  const loading = canRead && query.isLoading && !failure;
  const [sending, setSending] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const cur = inv?.currency ?? null;

  const downloadPdf = async () => {
    if (!inv) return;
    setPdfBusy(true);
    try {
      saveBlobAs(await fetchTruckingInvoicePdf(inv.id), `${inv.invoice_number || "invoice"}.pdf`);
    } catch (error) {
      toast.error(await getBlobApiErrorMessage(error, "The PDF could not be made."));
    } finally {
      setPdfBusy(false);
    }
  };

  if (!canRead) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <PageHeaderBand eyebrow="Trucking invoice" title="Invoice" onBack={() => navigate(-1)} backLabel="Back" />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)]">
          <p className="mx-auto max-w-[1920px] rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-10 text-center text-sm text-fieldiq-text">
            Invoices are visible to admin, office staff, supervisors and dispatchers.
          </p>
        </section>
      </div>
    );
  }

  const skin = statusSkin(inv?.status);
  const taxes = inv ? taxRows(inv) : [];

  return (
    <div data-testid="trucking-invoice-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={inv ? `Trucking invoice · ${skin.label}` : "Trucking invoice"}
        title={inv ? inv.invoice_number : loading ? "Loading…" : "Invoice"}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          inv ? (
            <>
              <button type="button" className={BAND_SECONDARY} onClick={downloadPdf} disabled={pdfBusy}>
                {pdfBusy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Download className="size-4" aria-hidden="true" />}
                Download PDF
              </button>
              {canWrite && VOIDABLE.includes(inv.status) && (
                <button type="button" className={BAND_SECONDARY} onClick={() => setVoiding(true)}>
                  <Ban className="size-4" aria-hidden="true" />
                  Void
                </button>
              )}
              {canWrite && SENDABLE.includes(inv.status) && (
                <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => setSending(true)}>
                  <Send className="size-4" aria-hidden="true" />
                  Send
                </button>
              )}
            </>
          ) : undefined
        }
        stats={
          failure || (!loading && !inv)
            ? undefined
            : [
                { icon: <Receipt className="size-4" aria-hidden="true" />, label: "Subtotal", sub: "Before tax", tone: "neutral", loading, value: fmtCurrency(inv?.subtotal, cur) },
                { icon: <Percent className="size-4" aria-hidden="true" />, label: "Tax", sub: inv?.jurisdiction ? `For ${inv.jurisdiction}` : "No jurisdiction", tone: "neutral", loading, value: fmtCurrency(inv?.tax_amount, cur) },
                { icon: <CircleDollarSign className="size-4" aria-hidden="true" />, label: "Total", sub: cur ?? "Currency —", tone: "accent", loading, value: fmtCurrency(inv?.total, cur) },
                { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: "Due", sub: "Due date", tone: "info", loading, value: formatCalendarDate(inv?.due_date ?? null) ?? "—" },
              ]
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {failure ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this invoice" />
            </div>
          ) : loading || !inv ? (
            <div className="h-64 animate-pulse rounded-fi-panel bg-fieldiq-card" aria-hidden="true" />
          ) : (
            <>
              {inv.status === "void" && (
                <section
                  data-testid="invoice-void"
                  aria-labelledby="invoice-void-title"
                  className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card p-4"
                >
                  <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Void</p>
                  <h2 id="invoice-void-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">This invoice is void</h2>
                  <dl className="mt-3 grid gap-x-8 gap-y-3 text-sm sm:grid-cols-3">
                    <Spec label="Reason"><span data-testid="void-reason">{inv.void_reason?.trim() || "—"}</span></Spec>
                    <Spec label="Voided by"><span data-testid="voided-by">{inv.voided_by_name?.trim() || "—"}</span></Spec>
                    <Spec label="Voided"><span data-testid="voided-at">{inv.voided_at ? formatInstant(inv.voided_at, timeZone) ?? "—" : "—"}</span></Spec>
                  </dl>
                  <p className="mt-3 text-xs text-fieldiq-muted">A void invoice is final; its BOLs can be invoiced again.</p>
                </section>
              )}
              <section className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
                <dl className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                  <Spec label="Status">
                    <span className={`inline-block rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink ${skin.fill}`}>{skin.label}</span>
                  </Spec>
                  <Spec label="Customer">{inv.customer_name || "—"}</Spec>
                  <Spec label={inv.invoice_type === "move_job" ? "Move job" : "Bill of lading"}>
                    {inv.invoice_type === "move_job"
                      ? inv.move_job_id
                        ? <Link className="underline underline-offset-2" to={`/trucking/move-jobs/${inv.move_job_id}`}>{inv.move_job_name || "—"}{inv.master_bol_number ? ` · ${inv.master_bol_number}` : ""}</Link>
                        : "—"
                      : inv.load_id
                        ? <Link className="underline underline-offset-2" to={`/trucking/bols/${inv.load_id}`}>{inv.bol_number ? `BOL ${inv.bol_number}` : "—"}</Link>
                        : "—"}
                  </Spec>
                  <Spec label="Jurisdiction">{inv.jurisdiction ? `${regionName(inv.jurisdiction)} (${inv.jurisdiction})` : "—"}</Spec>
                  <Spec label="Created">{formatInstantTenantDay(inv.created_at, timeZone) ?? "—"}</Spec>
                  <Spec label="Sent">
                    <span data-testid="invoice-sent">
                      {inv.sent_at
                        ? `${formatInstant(inv.sent_at, timeZone) ?? "—"} to ${inv.sent_to || "—"}${inv.sent_to_source && SENT_SOURCE[inv.sent_to_source] ? ` (${SENT_SOURCE[inv.sent_to_source]})` : ""}${inv.sent_cc && inv.sent_cc.length ? `, copied to ${inv.sent_cc.join(", ")}` : ""}`
                        : "Not sent"}
                    </span>
                  </Spec>
                  <Spec label="Paid">{inv.paid_at ? formatInstantTenantDay(inv.paid_at, timeZone) ?? "—" : "—"}</Spec>
                </dl>
              </section>

              {editing ? (
                <LinesEditor inv={inv} onDone={() => setEditing(false)} />
              ) : (
                <LinesPanel
                  inv={inv}
                  taxes={taxes}
                  editAction={
                    canWrite && inv.status === "draft" ? (
                      <button type="button" className={SECONDARY} onClick={() => setEditing(true)}>
                        <Pencil className="size-4" aria-hidden="true" />
                        Edit lines
                      </button>
                    ) : undefined
                  }
                />
              )}
            </>
          )}
        </div>
      </section>

      {canWrite && sending && inv && <SendDialog inv={inv} tz={timeZone} onClose={() => setSending(false)} />}
      {canWrite && voiding && inv && <VoidDialog inv={inv} onClose={() => setVoiding(false)} />}
    </div>
  );
}

function Spec({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-bold uppercase tracking-wider text-fieldiq-muted">{label}</dt>
      <dd className="mt-0.5 break-words text-fieldiq-text">{children}</dd>
    </div>
  );
}

function LinesPanel({ inv, taxes, editAction }: { inv: TruckingInvoice; taxes: { label: string; amount: string | null }[]; editAction?: React.ReactNode }) {
  const cur = inv.currency ?? null;
  return (
    <section
      aria-labelledby="invoice-lines-title"
      data-testid="invoice-lines"
      className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
    >
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
        <div>
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Invoice</p>
          <h2 id="invoice-lines-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Lines</h2>
        </div>
        {editAction}
      </header>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left">
          <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            <tr>
              <th scope="col" className="px-4 py-3">Description</th>
              <th scope="col" className="px-4 py-3">Type</th>
              <th scope="col" className="px-4 py-3 text-right">Quantity</th>
              <th scope="col" className="px-4 py-3 text-right">Rate</th>
              <th scope="col" className="px-4 py-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.line_items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-fieldiq-text">This invoice has no lines.</td></tr>
            ) : (
              inv.line_items.map((l) => (
                <tr key={l.id} data-testid="invoice-line" className="border-t border-fieldiq-border">
                  <td className="px-4 py-3 text-sm text-fieldiq-text">
                    {l.description || "—"}
                    {l.bol_number && <span className="ml-2 text-xs text-fieldiq-muted">BOL {l.bol_number}</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-fieldiq-text">{lineTypeLabel(l.line_type)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{num(l.quantity) === null ? "—" : `${Number(l.quantity)}${l.unit ? ` ${l.unit}` : ""}`}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{fmtCurrency(l.rate, cur)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{fmtCurrency(l.amount, cur)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <dl data-testid="invoice-totals" className="ml-auto max-w-sm space-y-1.5 border-t border-fieldiq-border px-4 py-4 text-sm">
        <TotalRow label="Subtotal" value={fmtCurrency(inv.subtotal, cur)} />
        {taxes.map((t) => <TotalRow key={t.label} label={t.label} value={fmtCurrency(t.amount, cur)} />)}
        <TotalRow strong label={`Total${cur ? ` (${cur})` : ""}`} value={fmtCurrency(inv.total, cur)} />
      </dl>
    </section>
  );
}

function TotalRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div data-testid="total-row" className={`flex justify-between gap-6 ${strong ? "border-t border-fieldiq-border pt-1.5 font-extrabold" : ""}`}>
      <dt className="text-fieldiq-text">{label}</dt>
      <dd className="font-mono text-fieldiq-text">{value}</dd>
    </div>
  );
}

type DraftLine = { key: string; source: TruckingInvoiceLine | null; description: string; quantity: string; rate: string };

function LinesEditor({ inv, onDone }: { inv: TruckingInvoice; onDone: () => void }) {
  const save = useSaveTruckingInvoiceLines();
  const [lines, setLines] = useState<DraftLine[]>(() =>
    inv.line_items.map((l) => ({ key: l.id, source: l, description: l.description ?? "", quantity: num(l.quantity) ?? "", rate: num(l.rate) ?? "" })),
  );
  const [refusal, setRefusal] = useState<{ error: string; problems: string[] } | null>(null);
  const set = (key: string, field: "description" | "quantity" | "rate", value: string) =>
    setLines((all) => all.map((l) => (l.key === key ? { ...l, [field]: value } : l)));

  const submit = async () => {
    setRefusal(null);
    // Rate and quantity go as typed: the API says which line is incomplete
    // (422 LINE_ITEM_INCOMPLETE), and an explicit 0 rate is a real $0.
    const lineItems: TruckingInvoiceLineWrite[] = lines.map((l) => ({
      lineType: l.source?.line_type ?? "other",
      description: l.description.trim() || null,
      quantity: l.quantity.trim(),
      unit: l.source?.unit ?? "flat",
      rate: l.rate.trim(),
      bolId: l.source?.bol_id ?? null,
      weightTicketId: l.source?.weight_ticket_id ?? null,
    }));
    try {
      await save.mutateAsync({ id: inv.id, lineItems });
      toast.success("Lines saved");
      onDone();
    } catch (error) {
      const data = (error as { response?: { data?: { problems?: unknown } } })?.response?.data;
      const problems = Array.isArray(data?.problems) ? data!.problems!.filter((p): p is string => typeof p === "string") : [];
      setRefusal({ error: getApiErrorMessage(error, "The lines were not saved."), problems });
    }
  };

  return (
    <section data-testid="invoice-lines-editor" className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
      <header className="border-b border-fieldiq-border px-4 py-4">
        <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Draft</p>
        <h2 className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Edit lines</h2>
        <p className="mt-1 text-xs text-fieldiq-muted">Every line needs a rate and a quantity. Totals are recalculated when you save.</p>
      </header>
      <div className="space-y-3 px-4 py-4">
        {lines.map((l, i) => (
          <div key={l.key} data-testid="draft-line" className="grid items-end gap-2 sm:grid-cols-[1fr_8rem_8rem_auto]">
            <label className="min-w-0 text-xs text-fieldiq-muted">
              Line {i + 1} description
              <input className={INPUT} value={l.description} onChange={(e) => set(l.key, "description", e.target.value)} />
            </label>
            <label className="text-xs text-fieldiq-muted">
              Quantity
              <input className={INPUT} inputMode="decimal" value={l.quantity} onChange={(e) => set(l.key, "quantity", e.target.value)} />
            </label>
            <label className="text-xs text-fieldiq-muted">
              Rate
              <input className={INPUT} inputMode="decimal" value={l.rate} onChange={(e) => set(l.key, "rate", e.target.value)} />
            </label>
            <button type="button" className={SECONDARY} aria-label={`Remove line ${i + 1}`} onClick={() => setLines((all) => all.filter((x) => x.key !== l.key))}>
              <Trash2 className="size-4" aria-hidden="true" />
            </button>
          </div>
        ))}
        <button
          type="button"
          className={SECONDARY}
          onClick={() => setLines((all) => [...all, { key: `new-${all.length}-${Date.now()}`, source: null, description: "", quantity: "", rate: "" }])}
        >
          <Plus className="size-4" aria-hidden="true" />
          Add line
        </button>
        {refusal && (
          <div role="alert" data-testid="lines-refusal" className="space-y-1 text-sm text-fieldiq-text">
            {refusal.problems.length > 0 ? refusal.problems.map((p) => <p key={p}>{p}</p>) : <p>{refusal.error}</p>}
          </div>
        )}
        <div className="flex gap-2">
          <button type="button" className="fi-action fi-action--standard fi-action--primary disabled:opacity-50" disabled={save.isPending} onClick={submit}>
            {save.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Save lines
          </button>
          <button type="button" className={SECONDARY} onClick={onDone}>Cancel</button>
        </div>
      </div>
    </section>
  );
}

function SendDialog({ inv, tz, onClose }: { inv: TruckingInvoice; tz: string | null; onClose: () => void }) {
  const preview = useTruckingInvoiceRecipient(inv.id, true);
  const send = useSendTruckingInvoice();
  const resolved = preview.data?.data?.to ?? null;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [ccText, setCcText] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  // Pre-fill once, from who the API would send to.
  useEffect(() => {
    if (!touched && resolved) {
      setEmail(resolved.email);
      setName(resolved.name ?? "");
    }
  }, [resolved, touched]);
  const cc = useMemo(() => parseCcInput(ccText), [ccText]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async () => {
    setRefusal(null);
    if (cc.problem) return;
    // A To left as the API resolved it is not sent, so the API records where
    // the address came from (accounting email / main contact). A typed one is.
    const typed = email.trim();
    const sameAsResolved = !!resolved && typed === resolved.email && (name.trim() || null) === (resolved.name ?? null);
    const body: { email?: string; contact_name?: string; cc?: string[] } = {};
    if (typed && !sameAsResolved) {
      body.email = typed;
      if (name.trim()) body.contact_name = name.trim();
    }
    if (cc.cc.length) body.cc = cc.cc;
    try {
      const sent = await send.mutateAsync({ id: inv.id, body });
      toast.success(`Sent to ${sent.sent_to}${sent.sent_cc && sent.sent_cc.length ? `, copied to ${sent.sent_cc.join(", ")}` : ""}`);
      onClose();
    } catch (error) {
      setRefusal(getApiErrorMessage(error, "The invoice was not sent."));
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={`Send ${inv.invoice_number}`} className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
        <header className="border-b border-fieldiq-border px-5 py-4">
          <h2 className="text-base font-extrabold tracking-tight text-fieldiq-text">Send {inv.invoice_number}</h2>
        </header>
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <RecipientPreview query={preview} tz={tz} subject="who this goes to" />
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="min-w-0 text-xs text-fieldiq-muted">
              To
              <input
                type="email"
                className={INPUT}
                value={email}
                onChange={(e) => { setTouched(true); setEmail(e.target.value); }}
              />
            </label>
            <label className="min-w-0 text-xs text-fieldiq-muted">
              Contact name
              <input className={INPUT} value={name} onChange={(e) => { setTouched(true); setName(e.target.value); }} />
            </label>
          </div>
          <CcField value={ccText} onChange={setCcText} problem={ccText.trim() ? cc.problem : null} />
          {refusal && <p role="alert" data-testid="send-refusal" className="text-sm font-medium text-fieldiq-text">{refusal}</p>}
        </div>
        <footer className="flex flex-wrap justify-end gap-2 border-t border-fieldiq-border px-5 py-3">
          <button type="button" className={SECONDARY} onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
            disabled={send.isPending || !!(ccText.trim() && cc.problem)}
            onClick={submit}
          >
            {send.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Send invoice
          </button>
        </footer>
      </div>
    </div>
  );
}

function VoidDialog({ inv, onClose }: { inv: TruckingInvoice; onClose: () => void }) {
  const voidIt = useVoidTruckingInvoice();
  const [reason, setReason] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async () => {
    setRefusal(null);
    try {
      await voidIt.mutateAsync({ id: inv.id, reason: reason.trim() });
      toast.success(`${inv.invoice_number} is void`);
      onClose();
    } catch (error) {
      setRefusal(getApiErrorMessage(error, "The invoice was not voided."));
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={`Void ${inv.invoice_number}`} className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
        <header className="border-b border-fieldiq-border px-5 py-4">
          <h2 className="text-base font-extrabold tracking-tight text-fieldiq-text">Void {inv.invoice_number}</h2>
        </header>
        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          <p className="text-sm text-fieldiq-text">Void is final. Its BOLs can then be invoiced again.</p>
          <label htmlFor="void-reason" className="block text-xs text-fieldiq-muted">
            Reason (required)
          </label>
          <textarea
            id="void-reason"
            rows={3}
            className="w-full min-w-0 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-2 py-1.5 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {refusal && <p role="alert" data-testid="void-refusal" className="text-sm font-medium text-fieldiq-text">{refusal}</p>}
        </div>
        <footer className="flex flex-wrap justify-end gap-2 border-t border-fieldiq-border px-5 py-3">
          <button type="button" className={SECONDARY} onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
            disabled={voidIt.isPending || !reason.trim()}
            onClick={submit}
          >
            {voidIt.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Void invoice
          </button>
        </footer>
      </div>
    </div>
  );
}
